import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ShoppingCart, Plus, Minus, Package2, Store, Search, Filter, X, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ProductPhotoService } from '../../services/productPhotoService';

interface Business {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  booking_settings?: {
    logo_url?: string;
    [key: string]: any;
  };
}

interface StockItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  quantity: number;
  is_sellable: boolean; // Keep for compatibility but don't filter by it
  category_id?: string;
  category?: {
    id: string;
    name: string;
  };
}

interface CartItem {
  product: StockItem;
  quantity: number;
}

export default function StoreOnline() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const navigate = useNavigate();
  
  console.log('🏪 StoreOnline component loaded with businessSlug:', businessSlug);
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [products, setProducts] = useState<StockItem[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  
  // Popular products banner state
  const [popularProducts, setPopularProducts] = useState<StockItem[]>([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [isCheckoutView, setIsCheckoutView] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    shippingMethod: ''
  });
  
  // Store design settings
  const [storeDesign, setStoreDesign] = useState({
    sidebarColor: '#374151', // Default gray-700
    sidebarTextColor: '#ffffff', // Default white
    headerColor: '#ffffff',  // Default white
    backgroundColor: '#f9fafb', // Default gray-50
    accentColor: '#3b82f6',   // Default blue-500
    businessNameColor: '#111827', // Default gray-900
    browseProductsColor: '#6b7280', // Default gray-500
    showBusinessLogo: true,   // Default show logo
    showBusinessName: true,   // Default show name
    showPopularProducts: false, // Default hide popular products
    popularProductIds: [],     // Default empty array
    displayStockLevels: false,   // Default hide stock levels
    hideOutOfStockProducts: false // Default show all products
  });

  // Shipping settings
  const [shippingSettings, setShippingSettings] = useState({
    enableInStorePickup: true,
    pickupAddress: '',
    shippingMethods: [] as Array<{name: string, price: number}>
  });

  // Load popular products based on selected IDs
  const loadPopularProducts = async (productIds: string[]) => {
    if (!productIds || productIds.length === 0) {
      setPopularProducts([]);
      return;
    }

    try {
      const { data: popularProductsData, error } = await supabase
        .from('stock_items')
        .select('id, name, description, price, quantity, is_sellable, category_id')
        .in('id', productIds);

      if (error) {
        console.error('🏪 Error loading popular products:', error);
        return;
      }

      // Sort products to match the order they were selected
      const sortedProducts = productIds
        .map(id => popularProductsData?.find(p => p.id === id))
        .filter(Boolean) as StockItem[];

      setPopularProducts(sortedProducts);
      console.log('🏪 Popular products loaded:', sortedProducts.length);
    } catch (error) {
      console.error('🏪 Error loading popular products:', error);
    }
  };

  useEffect(() => {
    if (businessSlug) {
      loadStoreData();
    }
  }, [businessSlug]);

  // Load popular products when design settings change
  useEffect(() => {
    if (storeDesign.showPopularProducts && storeDesign.popularProductIds.length > 0) {
      loadPopularProducts(storeDesign.popularProductIds);
    } else {
      setPopularProducts([]);
    }
  }, [storeDesign.showPopularProducts, storeDesign.popularProductIds]);

  // Auto-rotate banner every 5 seconds
  useEffect(() => {
    if (popularProducts.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex(prev => (prev + 1) % popularProducts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [popularProducts.length]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🏪 Loading store data for business slug:', businessSlug);

      // Load business information
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select('id, name, phone, email, address, booking_settings, slug')
        .eq('slug', businessSlug)
        .single();

      console.log('🏪 Business query result:', { businessData, businessError });

      if (businessError) {
        console.error('🏪 Business error:', businessError);
        throw new Error(`Store not found: ${businessError.message}`);
      }

      setBusiness(businessData);

      // Load store design settings
      try {
        const { data: designData, error: designError } = await supabase
          .from('online_store_settings')
          .select('sidebar_color, sidebar_text_color, header_color, background_color, accent_color, business_name_color, browse_products_color, show_business_logo, show_business_name, show_popular_products, popular_product_ids, display_stock_levels, hide_out_of_stock_products, enable_in_store_pickup, pickup_address, shipping_methods')
          .eq('business_id', businessData.id)
          .single();

        if (designError && designError.code !== 'PGRST116') {
          console.warn('🏪 Could not load design settings:', designError);
        } else if (designData) {
          console.log('🏪 Design settings loaded:', designData);
          setStoreDesign({
            sidebarColor: designData.sidebar_color || '#374151',
            sidebarTextColor: designData.sidebar_text_color || '#ffffff',
            headerColor: designData.header_color || '#ffffff',
            backgroundColor: designData.background_color || '#f9fafb',
            accentColor: designData.accent_color || '#3b82f6',
            businessNameColor: designData.business_name_color || '#111827',
            browseProductsColor: designData.browse_products_color || '#6b7280',
            showBusinessLogo: designData.show_business_logo ?? true,
            showBusinessName: designData.show_business_name ?? true,
            showPopularProducts: designData.show_popular_products ?? false,
            popularProductIds: designData.popular_product_ids || [],
            displayStockLevels: designData.display_stock_levels ?? false,
            hideOutOfStockProducts: designData.hide_out_of_stock_products ?? false
          });

          // Load shipping settings
          setShippingSettings({
            enableInStorePickup: designData.enable_in_store_pickup ?? true,
            pickupAddress: designData.pickup_address || '',
            shippingMethods: designData.shipping_methods || []
          });
        }
      } catch (designError) {
        console.warn('🏪 Error loading design settings:', designError);
        // Continue with defaults if design settings fail to load
      }

      // Load all products for this business (no sellable filter)
      const { data: productsData, error: productsError } = await supabase
        .from('stock_items')
        .select(`
          id, 
          name, 
          description, 
          price, 
          quantity, 
          is_sellable, 
          category_id,
          category:stock_categories(
            id,
            name
          )
        `)
        .eq('business_id', businessData.id)
        .order('name');

      if (productsError) {
        console.error('🏪 Products error:', productsError);
        throw productsError;
      }

      console.log('🏪 Products loaded:', productsData);
      console.log('🏪 Number of products found:', productsData?.length || 0);
      console.log('🏪 First product (if any):', productsData?.[0]);
      setProducts(productsData || []);

      // Load product photos
      if (productsData && productsData.length > 0) {
        const stockItemIds = productsData.map(product => product.id);
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          Object.entries(photoMap).forEach(([stockItemId, photo]) => {
            photoUrls[stockItemId] = photo.photo_url;
          });
          
          console.log('🏪 Product photos loaded:', Object.keys(photoUrls).length);
          setProductPhotos(photoUrls);
        } catch (photoError) {
          console.warn('🏪 Could not load product photos:', photoError);
          // Don't fail the entire load if photos fail
        }
      }

      // Derive categories from products (same approach as Stock page)
      if (productsData && productsData.length > 0) {
        const categoryMap = new Map();
        
        productsData.forEach(product => {
          const categoryId = product.category_id || 'uncategorized';
          const categoryName = product.category?.name || 'Uncategorized';
          
          if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, {
              id: categoryId,
              name: categoryName
            });
          }
        });
        
        const derivedCategories = Array.from(categoryMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
        
        console.log('🏪 Categories derived from products:', derivedCategories);
        console.log('🏪 Number of categories found:', derivedCategories.length);
        setCategories(derivedCategories);
      } else {
        setCategories([]);
      }

    } catch (err) {
      console.error('🏪 Error loading store data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: StockItem) => {
    if (product.quantity === 0) {
      toast.error('This product is currently out of stock');
      return;
    }

    // Set animation state
    setAddingToCart(product.id);
    
    // Clear animation after 600ms
    setTimeout(() => {
      setAddingToCart(null);
    }, 600);

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        if (existingItem.quantity < product.quantity) {
          return prevCart.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        } else {
          toast.error('Not enough stock available');
          return prevCart;
        }
      } else {
        return [...prevCart, { product, quantity: 1 }];
      }
    });
  };

  const updateCartQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity === 0) {
      removeFromCart(productId);
      return;
    }

    const product = products.find(p => p.id === productId);
    if (product && newQuantity > product.quantity) {
      toast.error('Not enough stock available');
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  const getCartSubtotal = () => {
    return cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const getCartGST = () => {
    const subtotal = getCartSubtotal();
    return subtotal * 0.05; // 5% GST
  };

  const getCartTotal = () => {
    return getCartSubtotal() + getCartGST();
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const isCheckoutFormValid = () => {
    const { firstName, lastName, email, phone, shippingMethod } = checkoutForm;
    return firstName.trim() && lastName.trim() && email.trim() && phone.trim() && shippingMethod.trim();
  };

  const handleCheckoutFormChange = (field: string, value: string) => {
    setCheckoutForm(prev => ({ ...prev, [field]: value }));
  };

  const handleProceedToCheckout = () => {
    setIsCheckoutView(true);
  };

  const handleBackToCart = () => {
    setIsCheckoutView(false);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery === '' || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const productCategoryId = product.category_id || 'uncategorized';
    const matchesCategory = selectedCategory === 'all' || 
      productCategoryId === selectedCategory;
    
    // Filter out out-of-stock products if the setting is enabled
    const hasStock = !storeDesign.hideOutOfStockProducts || product.quantity > 0;
    
    return matchesSearch && matchesCategory && hasStock;
  });

  console.log('🏪 Total products:', products.length);
  console.log('🏪 Filtered products:', filteredProducts.length);
  console.log('🏪 Search query:', searchQuery);
  console.log('🏪 Selected category:', selectedCategory);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: storeDesign.backgroundColor }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading store...</p>
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: storeDesign.backgroundColor }}
      >
        <div className="text-center max-w-md mx-auto p-6">
          <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Store Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The store you\'re looking for doesn\'t exist.'}</p>
          {businessSlug && (
            <div className="bg-gray-100 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <strong>Business Slug:</strong> {businessSlug}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Check the browser console for more details
              </p>
            </div>
          )}
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="h-screen flex flex-col"
      style={{ backgroundColor: storeDesign.backgroundColor }}
    >
      {/* Full Width Header */}
      <div 
        className="relative overflow-hidden flex-shrink-0"
        style={{ 
          background: `linear-gradient(135deg, ${storeDesign.headerColor} 0%, ${storeDesign.headerColor}f0 100%)`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        
        <div className="relative px-6 py-6">
          <div className="flex items-center justify-between">
            {/* Business Info */}
            <div className="flex items-center space-x-4">
              {storeDesign.showBusinessLogo && business?.booking_settings?.logo_url && (
                <div className="h-10 w-10 rounded-xl overflow-hidden flex-shrink-0 shadow-lg">
                  <img 
                    src={business.booking_settings.logo_url} 
                    alt="Business Logo" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {storeDesign.showBusinessName && business?.name && (
                <div>
                  <h1 
                    className="text-2xl font-bold"
                    style={{ color: storeDesign.businessNameColor }}
                  >
                    {business.name}
                  </h1>
                  <p 
                    className="text-sm"
                    style={{ color: storeDesign.browseProductsColor }}
                  >
                    Browse Products
                  </p>
                </div>
              )}
            </div>

            {/* Enhanced Search Bar */}
            <div className="flex-1 max-w-2xl mx-8 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-white/90 backdrop-blur-sm border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 transition-all duration-300 shadow-lg placeholder-gray-500"
              />
            </div>
            
            {/* Enhanced Cart Button */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative text-white px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2 shadow-lg backdrop-blur-sm"
              style={{ 
                backgroundColor: storeDesign.accentColor,
                boxShadow: `0 8px 25px ${storeDesign.accentColor}40`
              }}
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="font-semibold">Cart</span>
              {getCartItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg animate-pulse">
                  {getCartItemCount()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Area with Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Enhanced Left Sidebar - Categories */}
      <div 
        className="w-64 flex-shrink-0 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(180deg, ${storeDesign.sidebarColor} 0%, ${storeDesign.sidebarColor}f0 100%)`,
          color: storeDesign.sidebarTextColor,
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        {/* Categories Navigation */}
        <nav className="relative z-10 p-6 h-full">
          <h3 
            className="text-xs font-semibold uppercase tracking-wider mb-4 opacity-70"
            style={{ color: storeDesign.sidebarTextColor }}
          >
            Categories
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 text-sm font-semibold transform hover:scale-105 ${
                selectedCategory === 'all' 
                  ? 'text-white shadow-lg' 
                  : 'hover:bg-white hover:bg-opacity-10 hover:shadow-md'
              }`}
              style={selectedCategory === 'all' ? {
                backgroundColor: storeDesign.accentColor,
                color: '#ffffff',
                boxShadow: `0 4px 15px ${storeDesign.accentColor}40`
              } : {
                color: storeDesign.sidebarTextColor
              }}
            >
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
                <span>All Products</span>
              </div>
            </button>
            {categories.length === 0 ? (
              <div 
                className="text-xs px-3 py-2 opacity-70"
                style={{ color: storeDesign.sidebarTextColor }}
              >
                No categories found
              </div>
            ) : (
              categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 text-sm font-semibold transform hover:scale-105 ${
                    selectedCategory === category.id 
                      ? 'text-white shadow-lg' 
                      : 'hover:bg-white hover:bg-opacity-10 hover:shadow-md'
                  }`}
                  style={selectedCategory === category.id ? {
                    backgroundColor: storeDesign.accentColor,
                    color: '#ffffff',
                    boxShadow: `0 4px 15px ${storeDesign.accentColor}40`
                  } : {
                    color: storeDesign.sidebarTextColor
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
                    <span>{category.name}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </nav>
      </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">

        {/* Products Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Popular Products Banner - Only show when All Products is selected */}
          {storeDesign.showPopularProducts && selectedCategory === 'all' && popularProducts.length > 0 && (
            <div className="mb-6 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Light Header */}
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900">Featured Products</h2>
              </div>

              {/* Light Banner Content */}
              <div className="relative">
                <div className="relative h-52 overflow-hidden">
                  {popularProducts.map((product, index) => (
                    <div
                      key={product.id}
                      className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                        index === currentBannerIndex ? 'translate-x-0' : 
                        index < currentBannerIndex ? '-translate-x-full' : 'translate-x-full'
                      }`}
                    >
                      <div className="h-full flex items-center p-6 gap-6">
                        {/* Product Image */}
                        <div className="w-36 h-36 flex items-center justify-center flex-shrink-0">
                          {productPhotos[product.id] ? (
                            <img
                              src={productPhotos[product.id]}
                              alt={product.name}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Package2 className="h-12 w-12 text-gray-400" />
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {product.name}
                          </h3>
                          {product.description && (
                            <p className="text-gray-600 mb-4 line-clamp-2">
                              {product.description}
                            </p>
                          )}
                          <div className="flex items-center gap-8">
                            <div className="flex items-baseline gap-3">
                              <span 
                                className="text-3xl font-bold"
                                style={{ color: storeDesign.accentColor }}
                              >
                                ${product.price.toFixed(2)}
                              </span>
                              {product.quantity > 0 && (
                                <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded-md font-medium">
                                  In Stock
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => addToCart(product)}
                              disabled={product.quantity === 0}
                              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform ${
                                product.quantity === 0
                                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                  : addingToCart === product.id
                                  ? 'text-white scale-95 shadow-lg animate-pulse'
                                  : 'text-white hover:scale-105 shadow-lg'
                              }`}
                              style={product.quantity > 0 ? { 
                                backgroundColor: addingToCart === product.id ? '#10b981' : storeDesign.accentColor,
                                boxShadow: `0 4px 15px ${addingToCart === product.id ? '#10b981' : storeDesign.accentColor}30`
                              } : {}}
                            >
                              {product.quantity === 0 
                                ? 'Out of Stock' 
                                : addingToCart === product.id 
                                ? '✓ Added!' 
                                : 'Add to Cart'
                              }
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Light Navigation Dots */}
                {popularProducts.length > 1 && (
                  <div className="flex justify-center space-x-2 py-4">
                    {popularProducts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentBannerIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentBannerIndex 
                            ? 'opacity-100' 
                            : 'bg-gray-300 opacity-50 hover:opacity-75'
                        }`}
                        style={index === currentBannerIndex ? { 
                          backgroundColor: storeDesign.accentColor 
                        } : {}}
                      />
                    ))}
                  </div>
                )}

                {/* Light Navigation Arrows */}
                {popularProducts.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentBannerIndex(prev => 
                        prev === 0 ? popularProducts.length - 1 : prev - 1
                      )}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all flex items-center justify-center"
                    >
                      <ChevronLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => setCurrentBannerIndex(prev => 
                        (prev + 1) % popularProducts.length
                      )}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md transition-all flex items-center justify-center"
                    >
                      <ChevronRight className="h-5 w-5 text-gray-600" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Products Grid */}
          {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
            <p className="text-gray-600">
              {searchQuery || selectedCategory !== 'all' 
                ? 'Try adjusting your search or category filter.'
                : 'No products are currently available.'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredProducts.map(product => (
              <div key={product.id} className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 transform">
                {/* Enhanced Product Image */}
                <div className="relative h-32 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-300">
                  {productPhotos[product.id] ? (
                    <img
                      src={productPhotos[product.id]}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <Package2 className="h-8 w-8 text-gray-400 group-hover:text-gray-500 transition-colors duration-300" />
                  )}
                  {/* Subtle overlay on hover */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                </div>

                {/* Product Info */}
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 mb-1 text-sm line-clamp-1">{product.name}</h3>
                  {product.description && (
                    <p className="text-xs text-gray-600 mb-2 line-clamp-1">{product.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      ${product.price.toFixed(2)}
                    </span>
                    {storeDesign.displayStockLevels && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        product.quantity === 0 
                          ? 'text-red-600 bg-red-50' 
                          : 'text-green-600 bg-green-50'
                      }`}>
                        {product.quantity === 0 ? 'Out of Stock' : `${product.quantity} left`}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => addToCart(product)}
                    disabled={product.quantity === 0}
                    className={`w-full py-2.5 px-3 rounded-lg text-sm font-semibold transition-all duration-300 transform ${
                      product.quantity === 0
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : addingToCart === product.id
                        ? 'text-white scale-95 shadow-lg animate-pulse'
                        : 'text-white hover:scale-105 shadow-lg'
                    }`}
                    style={product.quantity > 0 ? { 
                      backgroundColor: addingToCart === product.id ? '#10b981' : storeDesign.accentColor,
                      boxShadow: `0 4px 15px ${addingToCart === product.id ? '#10b981' : storeDesign.accentColor}30`
                    } : {}}
                  >
                    {product.quantity === 0 
                      ? 'Out of Stock' 
                      : addingToCart === product.id 
                      ? '✓ Added!' 
                      : 'Add to Cart'
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Right Side Cart Modal */}
      {isCartOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="bg-white w-[28rem] h-full shadow-2xl flex flex-col transform translate-x-0 transition-transform duration-300 ease-out">
            {/* Close overlay - click outside to close */}
            <div 
              className="fixed inset-0 -z-10" 
              onClick={() => setIsCartOpen(false)}
            ></div>
            {/* Dynamic Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                {isCheckoutView ? (
                  <>
                    <button
                      onClick={handleBackToCart}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Checkout</h2>
                      <p className="text-sm text-gray-500">Complete your order</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: storeDesign.accentColor + '20' }}
                    >
                      <ShoppingCart 
                        className="h-5 w-5" 
                        style={{ color: storeDesign.accentColor }}
                      />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Shopping Cart</h2>
                      <p className="text-sm text-gray-500">{getCartItemCount()} {getCartItemCount() === 1 ? 'item' : 'items'}</p>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Dynamic Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isCheckoutView ? (
                /* Checkout Form */
                <div className="space-y-6">
                  {/* Customer Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                    
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={checkoutForm.firstName}
                          onChange={(e) => handleCheckoutFormChange('firstName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter first name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={checkoutForm.lastName}
                          onChange={(e) => handleCheckoutFormChange('lastName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter last name"
                        />
                      </div>
                    </div>

                    {/* Contact Fields */}
                    <div className="grid grid-cols-1 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={checkoutForm.email}
                          onChange={(e) => handleCheckoutFormChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={checkoutForm.phone}
                          onChange={(e) => handleCheckoutFormChange('phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Shipping Method */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Method</h3>
                    <div className="space-y-3">
                      {/* In-Store Pickup - Only show if enabled */}
                      {shippingSettings.enableInStorePickup && (
                        <div 
                          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                            checkoutForm.shippingMethod === 'pickup' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          onClick={() => handleCheckoutFormChange('shippingMethod', 'pickup')}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-4 h-4 rounded-full border-2 ${
                                checkoutForm.shippingMethod === 'pickup' 
                                  ? 'border-blue-500 bg-blue-500' 
                                  : 'border-gray-300'
                              }`}>
                                {checkoutForm.shippingMethod === 'pickup' && (
                                  <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">In-Store Pickup</h4>
                                <p className="text-sm text-gray-600">Pick up your order at our location</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {shippingSettings.pickupAddress || business?.address || 'Pickup address not set'}
                                </p>
                              </div>
                            </div>
                            <span className="text-lg font-semibold text-green-600">FREE</span>
                          </div>
                        </div>
                      )}

                      {/* Additional Shipping Methods */}
                      {shippingSettings.shippingMethods.map((method, index) => (
                        <div 
                          key={index}
                          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                            checkoutForm.shippingMethod === `shipping-${index}` 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          onClick={() => handleCheckoutFormChange('shippingMethod', `shipping-${index}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-4 h-4 rounded-full border-2 ${
                                checkoutForm.shippingMethod === `shipping-${index}` 
                                  ? 'border-blue-500 bg-blue-500' 
                                  : 'border-gray-300'
                              }`}>
                                {checkoutForm.shippingMethod === `shipping-${index}` && (
                                  <div className="w-full h-full rounded-full bg-white scale-50"></div>
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">{method.name}</h4>
                                <p className="text-sm text-gray-600">Delivery to your address</p>
                              </div>
                            </div>
                            <span className="text-lg font-semibold text-gray-900">${method.price.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}

                      {/* No shipping methods available */}
                      {!shippingSettings.enableInStorePickup && shippingSettings.shippingMethods.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p>No shipping methods available</p>
                          <p className="text-sm mt-1">Please contact the store for assistance</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* Cart Items */
                cart.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map(item => (
                      <div key={item.product.id} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          {productPhotos[item.product.id] ? (
                            <img
                              src={productPhotos[item.product.id]}
                              alt={item.product.name}
                              className="w-full h-full object-contain rounded-lg"
                            />
                          ) : (
                            <Package2 className="h-6 w-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{item.product.name}</h4>
                          <p className="text-sm text-gray-600">${item.product.price.toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                            className="p-1 rounded-full bg-gray-200 hover:bg-gray-300"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                            className="p-1 rounded-full bg-gray-200 hover:bg-gray-300"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">
                            ${(item.product.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>

            {/* Enhanced Cart Footer */}
            {cart.length > 0 && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 space-y-4">
                {/* Price Breakdown */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>Subtotal:</span>
                    <span>${getCartSubtotal().toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-gray-600">
                    <span>GST (5%):</span>
                    <span>${getCartGST().toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-blue-600">
                        ${getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
                {isCheckoutView ? (
                  <button 
                    onClick={() => {/* Handle payment */}}
                    disabled={!isCheckoutFormValid()}
                    className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 transform shadow-lg ${
                      isCheckoutFormValid() 
                        ? 'text-white hover:scale-105' 
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                    style={{ 
                      backgroundColor: isCheckoutFormValid() ? storeDesign.accentColor : '#d1d5db',
                      boxShadow: isCheckoutFormValid() ? `0 4px 15px ${storeDesign.accentColor}30` : 'none'
                    }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Pay Now</span>
                      <ChevronRight className="h-5 w-5" />
                    </div>
                  </button>
                ) : (
                  <button 
                    onClick={handleProceedToCheckout}
                    className="w-full text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                    style={{ 
                      backgroundColor: storeDesign.accentColor,
                      boxShadow: `0 4px 15px ${storeDesign.accentColor}30`
                    }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Proceed to Checkout</span>
                      <ChevronRight className="h-5 w-5" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
